import React, { useState } from "react";
import { Container, Card, Form, Button, Row, Col, Modal, Badge } from "react-bootstrap";
import { Reply, PencilSquare, Trash } from "react-bootstrap-icons";
import "./MemoAppPage.css"

// メモの型定義
interface Memo {
    id: number; // メモの一意のID
    parent_id?: number; // 親メモID（返信元）
    content: string; // メモ内容
    isUser: boolean; // ユーザーのメモかどうか
}

const MemoAppPage: React.FC = () => {
    /**
     * アプリの状態
     */
    const [memos, setMemos] = useState<Memo[]>([]); // メモのリスト
    const [newMemo, setNewMemo] = useState<string>(""); // 新規メモの入力内容
    const [selectedMemo, setSelectedMemo] = useState<Memo | null>(null); // 選択されたメモ

    /**
     * モーダルの状態管理
     */
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showReplyModal, setShowReplyModal] = useState(false);

    /**
     * 編集・返信の入力内容
     */
    const [editedContent, setEditedContent] = useState<string>(""); // 編集内容
    const [replyContent, setReplyContent] = useState<string>(""); // 返信内容

    /**
     * モーダルを閉じる処理
     */
    const closeModal = (setShow: React.Dispatch<React.SetStateAction<boolean>>) => {
        setShow(false);
        setSelectedMemo(null); // 選択状態をリセット
    };

    /**
     * メモの追加
     */
    const handleAddMemo = () => {
        if (!newMemo.trim()) return; // 入力が空白の場合は何もしない

        setMemos((prevMemos) => [
            ...prevMemos,
            { id: Date.now(), content: newMemo, isUser: true },
        ]);
        setNewMemo(""); // 入力をリセット
    };

    /**
     * メモの削除
     */
    const handleDeleteMemo = () => {
        if (!selectedMemo) return; // 選択されていなければ何もしない

        setMemos((prevMemos) => prevMemos.filter((memo) => memo.id !== selectedMemo.id));
        closeModal(setShowDeleteModal); // モーダルを閉じる
    };

    /**
     * メモの編集
     */
    const handleEditMemo = () => {
        if (!selectedMemo) return;

        setMemos((prevMemos) =>
            prevMemos.map((memo) =>
                memo.id === selectedMemo.id ? { ...memo, content: editedContent } : memo
            )
        );
        closeModal(setShowEditModal); // モーダルを閉じる
        setEditedContent(""); // 編集内容をリセット
    };

    /**
     * メモの返信
     */
    const handleReplyMemo = () => {
        if (!selectedMemo) return;

        setMemos((prevMemos) => [
            ...prevMemos,
            { id: Date.now(), parent_id: selectedMemo.id, content: replyContent, isUser: true },
        ]);
        closeModal(setShowReplyModal); // モーダルを閉じる
        setReplyContent(""); // 返信内容をリセット
    };

    /**
     * UI構成
     */
    return (
        <Container fluid className="MemoAppRoot d-flex justify-content-center align-items-center vh-100 bg-light">
            {/* メモアプリのメインカード */}
            <Card style={{ width: "100%", maxWidth: "500px", height: "95%" }}>
                <Card.Header className="bg-primary text-white text-center">
                    <h5>SNS風メモアプリ</h5>
                </Card.Header>

                {/* メモリスト */}
                <Card.Body className="overflow-auto d-flex flex-column bg-light">
                    {memos.map((memo) => (
                        <Row
                            key={memo.id}
                            className={`mb-3 ${memo.isUser ? "justify-content-end" : "justify-content-start"}`}
                        >
                            <Col xs="auto" style={{ maxWidth: "70%" }}>
                                <Card
                                    className={`p-2 ${memo.isUser ? "bg-success text-white" : "bg-secondary text-white"}`}
                                    style={{
                                        borderRadius: "15px",
                                        wordBreak: "break-word",
                                        textAlign: "left",
                                        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                                    }}
                                >
                                    <Card.Body>{/* parent_idが存在する場合、親メモを引用表示 */}
                                        {memo.parent_id && (
                                            <Card
                                                className="mb-2 p-2"
                                                style={{
                                                    backgroundColor: "#f8f9fa", // 引用部分の背景色を薄く
                                                    borderLeft: "4px solid #ccc", // 左側に強調線を追加
                                                    fontStyle: "italic", // イタリックで表示
                                                    color: "#555", // 文字色を薄く
                                                }}
                                            >
                                                <div>
                                                    {/* 親メモのIDを表示 */}
                                                    <small style={{ display: "block", color: "#999", marginBottom: "4px" }}>
                                                        引用ID: {memo.parent_id}
                                                    </small>
                                                    {/* 親メモの内容を表示 */}
                                                    {memos.find((parent) => parent.id === memo.parent_id)?.content || "元のメモが削除されました"}
                                                </div>
                                            </Card>
                                        )}
                                        {/* 現在のメモ内容を表示 */}
                                        {memo.content}</Card.Body>
                                    <Card.Footer className="d-flex justify-content-end">
                                        {/* メモIDを表示 */}
                                        <Badge bg="secondary" className="me-2 d-flex align-items-center text-wrap">
                                            ID: {memo.id}
                                        </Badge>
                                        {/* 返信ボタン */}
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="me-2 p-1 border-0"
                                            onClick={() => {
                                                setSelectedMemo(memo);
                                                setShowReplyModal(true);
                                            }}
                                        >
                                            <Reply size={20} color="green" /> {/* アイコンの色を調整 */}
                                        </Button>

                                        {/* 編集ボタン */}
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="me-2 p-1 border-0"
                                            onClick={() => {
                                                setSelectedMemo(memo);
                                                setEditedContent(memo.content);
                                                setShowEditModal(true);
                                            }}
                                        >
                                            <PencilSquare size={20} color="blue" /> {/* アイコンの色を調整 */}
                                        </Button>

                                        {/* 削除ボタン */}
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="p-1 border-0"
                                            onClick={() => {
                                                setSelectedMemo(memo);
                                                setShowDeleteModal(true);
                                            }}
                                        >
                                            <Trash size={20} color="red" /> {/* アイコンの色を調整 */}
                                        </Button>
                                    </Card.Footer>
                                </Card>
                            </Col>
                        </Row>
                    ))}
                </Card.Body>

                {/* 新規メモの入力エリア */}
                <Card.Footer className="bg-white">
                    <Form className="d-flex">
                        <Form.Control
                            type="text"
                            placeholder="メモを入力..."
                            value={newMemo}
                            onChange={(e) => setNewMemo(e.target.value)}
                            className="me-2"
                        />
                        <Button variant="primary" onClick={handleAddMemo}>
                            送信
                        </Button>
                    </Form>
                </Card.Footer>
            </Card>

            {/* 各種モーダル */}
            <Modal show={showDeleteModal} onHide={() => closeModal(setShowDeleteModal)} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>削除</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    削除したら元に戻すことはできません。
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => closeModal(setShowDeleteModal)}>
                        キャンセル
                    </Button>
                    <Button variant="primary" onClick={handleDeleteMemo}>
                        削除
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showEditModal} onHide={() => closeModal(setShowEditModal)} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>編集</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => closeModal(setShowEditModal)}>
                        キャンセル
                    </Button>
                    <Button variant="primary" onClick={handleEditMemo}>
                        保存
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showReplyModal} onHide={() => closeModal(setShowReplyModal)} backdrop="static">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>返信</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => closeModal(setShowReplyModal)}>
                        キャンセル
                    </Button>
                    <Button variant="primary" onClick={handleReplyMemo}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default MemoAppPage;
